import { zodResolver } from '@hookform/resolvers/zod'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { DialogContent, InputAdornment, Stack, Tooltip } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import FormDialogActions from '@/components/feedback/FormDialogActions'
import SwitchController from '@/components/inputs/SwitchController'
import TextFieldController from '@/components/inputs/TextFieldController'
import { getAssignResourceToActivationGroupSchema } from '@/features/activationGroup/schemas'
import type { AssignResourceToActivationGroup } from '@/features/activationGroup/types'

type AssignResourceDialogFormProps = {
  isLoading: boolean
  showActivatable: boolean
  onCancel: () => void
  onSubmit: (data: AssignResourceToActivationGroup) => void
}

/**
 * Contains all the logic related to the form to assign a new resource to an existing activation group.
 *
 * It is used within the AssignResourceDialog component.
 */
const AssignResourceDialogForm = ({
  isLoading,
  showActivatable,
  onCancel,
  onSubmit,
}: AssignResourceDialogFormProps) => {
  const { t } = useTranslation()
  const form = useForm({
    defaultValues: {
      resourceUuid: '',
      qualifiedCapacity: 0,
      activationPriority: null,
      isActivatable: true,
    },
    resolver: zodResolver(getAssignResourceToActivationGroupSchema(t)),
  })

  function handleSubmit(data: AssignResourceToActivationGroup) {
    onSubmit(data)
  }

  return (
    <FormProvider {...form}>
      <form
        noValidate
        aria-label={t('activation_groups.assign_resource_form.title')}
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextFieldController
            required
            label={t('activation_groups.assign_resource_form.resource_label')}
            name="resourceUuid"
            type="text"
          />

          <TextFieldController
            required
            label={t('activation_groups.assign_resource_form.qualified_capacity_label')}
            name="qualifiedCapacity"
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">Watts</InputAdornment>,
              },
            }}
            type="number"
          />

          <TextFieldController
            label={t('activation_groups.assign_resource_form.priority_label')}
            name="activationPriority"
            type="number"
          />

          {showActivatable && (
            <Stack alignItems="center" direction="row" gap={0.5}>
              <SwitchController
                label={t('activation_groups.assign_resource_form.is_activatable_label')}
                name="isActivatable"
              />
              <Tooltip title={t('activation_groups.assign_resource_form.is_activatable_help')}>
                <InfoOutlinedIcon color="action" fontSize="small" sx={{ cursor: 'help' }} />
              </Tooltip>
            </Stack>
          )}
        </DialogContent>

        <FormDialogActions isLoading={isLoading} onCancel={onCancel} />
      </form>
    </FormProvider>
  )
}

export default AssignResourceDialogForm
